import {
  Drawer,
  Box,
  IconButton,
  Grid,
  Stack,
  Autocomplete,
  TextField,
  Button,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CreateNewDefinition from "./CreateNewDefinition";
import DrawerSkeleton from "../helpers/components/DrawerSkeleton";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import {
  setSelectedDestinationTableCreateScreen,
  setSelectedInterfaceCreateScreen,
  setUniqueDefinitionData,
} from "../../../../../features/uniqueDefSlice";
import { useState } from "react";
import {
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../../features/userSlice";
import { UniqueDefinitionsEndpointLogic } from "../../../../../logic/DataCollection/Unifier/UniqueDefinitionsEndpointLogic";
import SimpleBackdrop from "../../../../BackDrop/BackDrop";

interface Props {
  open: boolean;
  onClose: () => void;
  loading: boolean;
}
type ForceRefreshResult = {
  filteringPayload: string | null;
  lookupPayload: string | null;
};

const CreateNewDefinitionSidebar = ({ open, onClose, loading }: Props) => {
  const dispatch = useDispatch();
  const {
    interfaceData,
    destinationTablesData,
    selectedInterfaceCreateScreen,
    selectedDestinationTableCreateScreen,
    lookupFieldDataForPayload,
    rowFilteringDataForPayload,
    isValidRowFiltering,
    isPreviewEmpty,
    isLookupFieldsValid,
  } = useSelector((state: any) => state.uniqueDefinition);
  const [isCreating, setIsCreating] = useState(false);
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const [forceRefresh, setForceRefresh] = useState<
    null | (() => Promise<ForceRefreshResult>)
  >(null);

  const destTableID: any =
    selectedDestinationTableCreateScreen &&
    selectedDestinationTableCreateScreen?.id;
  const intID =
    selectedInterfaceCreateScreen && selectedInterfaceCreateScreen?.id;

  const selectedInterfaceValue = selectedInterfaceCreateScreen
    ? {
        label: selectedInterfaceCreateScreen.affiliate_system_interface_name,
        id: selectedInterfaceCreateScreen.id,
      }
    : null;

  const selectedDestinationTableValue = selectedDestinationTableCreateScreen
    ? {
        label: selectedDestinationTableCreateScreen.destination_table,
        id: selectedDestinationTableCreateScreen.id,
      }
    : null;
  const handleInterfaceChange = (_: any, value: any, reason: string) => {
    const selectedTable = value
      ? interfaceData?.find((item: any) => item.id === value.id)
      : null;

    dispatch(setSelectedInterfaceCreateScreen(selectedTable));

    if (reason === "clear") {
      dispatch(setUniqueDefinitionData([]));
    }
  };

  const handleDestinationTableChange = (_: any, value: any, reason: string) => {
    const selectedTable = value
      ? destinationTablesData?.find((item: any) => item.id === value.id)
      : null;

    dispatch(setSelectedDestinationTableCreateScreen(selectedTable));

    // Update URL parameters

    if (reason === "clear") {
      dispatch(setUniqueDefinitionData([]));
    }
  };
  const handleCreateUniqueDefinition = async () => {
    if (!forceRefresh) return;
    const { filteringPayload, lookupPayload } = await forceRefresh();
    // Validation
    if (!intID) {
      dispatch(
        setDisplayErrorSnackBar({
          message: "Please select an interface.",
          type: "error",
        }),
      );
      return;
    }

    if (!destTableID) {
      dispatch(
        setDisplayErrorSnackBar({
          message: "Please select a destination table.",
          type: "error",
        }),
      );
      return;
    }

    if (!rowFilteringDataForPayload) {
      dispatch(
        setDisplayErrorSnackBar({
          message: "Filtering definition is required.",
          type: "error",
        }),
      );
      return;
    }

    if (!lookupFieldDataForPayload) {
      dispatch(
        setDisplayErrorSnackBar({
          message: "Unique definition is required.",
          type: "error",
        }),
      );
      return;
    }

    setIsCreating(true);
    try {
      const payload = {
        filtering_definition: filteringPayload,
        unique_definition: lookupPayload,
        backend_interface_id: Number(intID),
        destination_table_id: Number(destTableID),
      };

      const response =
        await UniqueDefinitionsEndpointLogic.createUnifierUniqueDefinition(
          oauthTokenHeader,
          payload,
        );

      if (response?.status === 201) {
        dispatch(
          setDisplayErrorSnackBar({
            message: "Unique definition created successfully!",
            type: "success",
          }),
        );
        onClose();
        await UniqueDefinitionsEndpointLogic.getAllUnifierUniqueDefinitions(
          oauthTokenHeader,
        );
      } else {
        throw new Error(
          response?.reason || "Failed to create unique definition",
        );
      }
    } catch (error: any) {
      console.error("Error creating unique definition:", error);
      dispatch(
        setDisplayErrorSnackBar({
          message:
            error?.reason ||
            error?.message ||
            "Failed to create unique definition",
          type: "error",
        }),
      );
    } finally {
      setIsCreating(false);
    }
  };
  return (
    <Drawer
      anchor="right"
      sx={{ zIndex: 1300 }}
      open={open}
      onClose={(_, reason) => {
        if (reason === "backdropClick") return;
        onClose();
      }}
      PaperProps={{
        sx: {
          width: "50%",
        },
      }}
    >
      {loading ? (
        <DrawerSkeleton />
      ) : (
        <Box
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Paper
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
              pb: 2,
              px: 2,
            }}
          >
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>

            <Grid container spacing={2} sx={{ mt: 1, pl: 1 }}>
              <Grid item xs={12} sm={5}>
                <Stack spacing={1}>
                  <Autocomplete
                    value={selectedInterfaceValue?.label || null}
                    options={
                      interfaceData?.map((item: any) => ({
                        label: item.affiliate_system_interface_name,
                        id: item.id,
                      })) || []
                    }
                    onChange={handleInterfaceChange}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        data-tracker="381"
                        placeholder="Search interface or choose from the list"
                        label="Interface"
                        required
                        InputProps={{
                          ...params.InputProps,
                        }}
                        color="secondary"
                        size="small"
                        variant="standard"
                      />
                    )}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} sm={5}>
                <Stack spacing={1}>
                  <Autocomplete
                    value={selectedDestinationTableValue?.label || null}
                    options={
                      destinationTablesData?.map((item: any) => ({
                        label: item.destination_table,
                        id: item.id,
                      })) || []
                    }
                    onChange={handleDestinationTableChange}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        data-tracker="382"
                        placeholder="Search table or choose from the list"
                        label="Destination Table"
                        required
                        InputProps={{
                          ...params.InputProps,
                        }}
                        color="secondary"
                        size="small"
                        variant="standard"
                      />
                    )}
                  />
                </Stack>
              </Grid>
              <Grid
                item
                xs={12}
                sm={2}
                sx={{
                  justifyContent: "flex-end",
                  alignItems: "flex-start",
                  display: "flex",
                }}
              >
                <Button
                  data-tracker="390"
                  variant="contained"
                  color="secondary"
                  sx={{ whiteSpace: "nowrap" }}
                  onClick={handleCreateUniqueDefinition}
                  disabled={
                    isCreating ||
                    !rowFilteringDataForPayload ||
                    isPreviewEmpty ||
                    !isLookupFieldsValid ||
                    !intID ||
                    !destTableID ||
                    !isValidRowFiltering
                  }
                >
                  <SaveOutlinedIcon sx={{ width: "25px", mr: "5px" }} />
                  Save
                  {isCreating && <SimpleBackdrop />}
                </Button>
              </Grid>
            </Grid>
          </Paper>

          <Box sx={{ flex: 1, overflowY: "auto" }}>
            <CreateNewDefinition onRegisterForceRefresh={setForceRefresh} />
          </Box>
        </Box>
      )}
    </Drawer>
  );
};

export default CreateNewDefinitionSidebar;
